import type { CompanyListItem } from "@/models/company.model";
import { showAlertCallout } from "@/shared/alerts";
import { useCompanyRepository } from "@/shared/contexts/repository-context";
import { ERROR_DISPLAY_TYPES } from "@/shared/utils/errors/error.types";
import {
  getTranslationKeyAndDisplayTypes,
  hasErrorDisplayType,
} from "@/shared/utils/errors/get-http-error-type";
import { showHttpErrorAlertCallout } from "@/shared/utils/show-http-error-alert-callout";
import { getGetCompanyListQueryKey } from "@/spz-api/companies/companies";
import {
  getGetCompanyGroupListPaginatedQueryKey,
  updateCompanyGroup,
  useCreateCompanyGroup,
} from "@/spz-api/company-groups/company-groups";
import type {
  UpdateCompanyGroupRequestDto,
  UpdateCompanyGroupResponseDto,
} from "@/spz-api/model";
import { zodResolver } from "@hookform/resolvers/zod";
import type { TreeNode } from "@spz/ui/components/checkbox-tree";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { COMPANY_GROUP_FORM_DEFAULT_VALUES } from "../forms/constants/company-group-form.constants";
import {
  type CompanyGroupFormSchemaValues,
  companyGroupFormSchema,
} from "../forms/schemas/company-group-form.schema";

interface UseCompanyGroupFormProps {
  initialValues?: CompanyGroupFormSchemaValues;
  groupId?: string;
  onSuccess?: () => void;
}
type UpdateCompanyGroupMutationVariables = {
  id: string;
  data: UpdateCompanyGroupRequestDto;
  headers?: Record<string, string>;
};
export const useCompanyGroupForm = ({
  initialValues,
  groupId,
  onSuccess,
}: UseCompanyGroupFormProps) => {
  const defaultValues: CompanyGroupFormSchemaValues = {
    ...COMPANY_GROUP_FORM_DEFAULT_VALUES,
    ...initialValues,
  };
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const groupCompanySchema = companyGroupFormSchema(t);
  const companyRepository = useCompanyRepository();
  const { data: companyList, isLoading: isLoadingCompanies } = useQuery<
    CompanyListItem[]
  >({
    queryKey: getGetCompanyListQueryKey(),
    queryFn: () => companyRepository.getCompanies(),
  });

  const form = useForm<CompanyGroupFormSchemaValues>({
    resolver: zodResolver(groupCompanySchema),
    defaultValues,
    mode: "onBlur",
  });

  const updateUserGroupMutation = useMutation<
    UpdateCompanyGroupResponseDto,
    unknown,
    UpdateCompanyGroupMutationVariables
  >({
    mutationFn: async ({ id, data, headers }) => {
      return updateCompanyGroup(id, data, {
        headers: { ...headers, "Content-Type": "application/json" },
      });
    },
    onSuccess: () => {
      showAlertCallout({
        type: "success",
        title: t("update_group_success"),
      });

      queryClient.invalidateQueries({
        queryKey: getGetCompanyGroupListPaginatedQueryKey({}),
      });

      onSuccess?.();
    },
    onError: (error: unknown) => {
      const { translationKey, displayTypes } =
        getTranslationKeyAndDisplayTypes(error);
      const message = t(translationKey);
      if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.FORM)) {
        form.setError("name", {
          type: "manual",
          message,
        });
      }
      if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.TOAST)) {
        showHttpErrorAlertCallout({
          error: message,
        });
      }
      queryClient.invalidateQueries({
        queryKey: getGetCompanyGroupListPaginatedQueryKey({}),
      });
    },
  });

  const createCompanyGroupMutation = useCreateCompanyGroup({
    mutation: {
      onSuccess: () => {
        showAlertCallout({
          type: "success",
          title: t("create_group_success"),
        });

        queryClient.invalidateQueries({
          queryKey: getGetCompanyGroupListPaginatedQueryKey({}),
        });

        onSuccess?.();
      },
      onError: (error: unknown) => {
        const { translationKey, displayTypes } =
          getTranslationKeyAndDisplayTypes(error);
        const message = t(translationKey);
        if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.FORM)) {
          form.setError("name", {
            type: "manual",
            message,
          });
        }
        if (hasErrorDisplayType(displayTypes, ERROR_DISPLAY_TYPES.TOAST)) {
          showHttpErrorAlertCallout({
            error: message,
          });
        }
      },
    },
  });

  const onSubmit = async (data: CompanyGroupFormSchemaValues) => {
    if (groupId) {
      const updatedAt = form.getValues().updatedAt;
      updateUserGroupMutation.mutate({
        id: groupId,
        data: {
          name: data.name.trim(),
          description: data.description?.trim() ?? null,
          companyIds: data.companies,
        },
        headers: updatedAt ? { "if-match": updatedAt } : undefined,
      });
    } else {
      createCompanyGroupMutation.mutate({
        data: {
          name: data.name.trim(),
          description: data.description?.trim(),
          companyIds: data.companies,
        },
      });
    }
  };

  const companyOptions = useMemo(() => {
    const options = Array.isArray(companyList)
      ? companyList.map<TreeNode>((item) => ({
          id: item.id,
          label: item.name,
        }))
      : [];

    return options;
  }, [companyList]);

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isLoading: form.formState.isSubmitting,
    companies: {
      options: companyOptions,
      loading: isLoadingCompanies,
    },
  };
};
